import React, { useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import { Button, Checkbox, Row, SearchIcon, Spinner, TextField, Typography } from "@coolremote/shared";
import { fetchModbusLibraryTemplate, useGetModbusLibraryTemplates } from "../../../../hooks/useModbusLibraryTemplatesApi";
import { useCreateModbusDeviceTemplate, useGetSiteModbusDeviceTemplates } from "../../../../hooks/useModbusDeviceTemplatesApi";
import { useCreateModbusFunctionalTemplate, useGetSiteModbusFunctionalTemplates } from "../../../../hooks/useModbusFunctionalTemplatesApi";
import { useCreateModbusUiTemplate, useGetSiteModbusUiTemplates } from "../../../../hooks/useModbusUiTemplatesApi";
import { useStoreState } from "../../../../models/RootStore";
import { IModbusLibraryTemplate, ModbusLibraryTemplateType } from "../../types";
import LibraryActionBanner, { IBannerState } from "./LibraryActionBanner";
import { dedupeImportedName } from "./libraryNaming";
import styles from "./ModbusSiteLibraryManagementDialog.module.css";

interface IProps {
  siteId?: string;
  active: boolean;
  /** Lets the dialog block closing (and tab switching) while a download is in flight. */
  onBusyChange?: (busy: boolean) => void;
}

const TYPE_LABEL: Record<ModbusLibraryTemplateType, string> = {
  device: t`Device`,
  functionalUnit: t`Functional Unit`,
  ui: t`Control UI`,
};

const BROWSE_MODES: { value: ModbusLibraryTemplateType; label: string }[] = [
  { value: "device", label: t`Devices` },
  { value: "functionalUnit", label: t`Functional Units` },
  { value: "ui", label: t`Control UI` },
];

const matchesSearch = (item: IModbusLibraryTemplate, search: string): boolean => {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return (
    item.name.toLowerCase().includes(needle) ||
    (item.manufacturer ?? "").toLowerCase().includes(needle) ||
    (item.model ?? "").toLowerCase().includes(needle) ||
    (item.description ?? "").toLowerCase().includes(needle)
  );
};

const CoolAutomationLibraryTab: React.FC<IProps> = ({ siteId, active, onBusyChange }) => {
  const getSite = useStoreState((s) => s.sites.getSite);
  const [selectedTypes, setSelectedTypes] = useState<Set<ModbusLibraryTemplateType>>(new Set());
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState<IBannerState | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{
    done: number;
    total: number;
    jobsDone: number;
    jobsTotal: number;
  } | null>(null);

  useEffect(() => {
    onBusyChange?.(downloadProgress !== null);
  }, [downloadProgress, onBusyChange]);

  useEffect(() => {
    if (!active && downloadProgress === null) {
      setSearch("");
      setSelectedTypes(new Set());
      setSelectedIds(new Set());
      setBanner(null);
    }
  }, [active]);

  const { data, isLoading } = useGetModbusLibraryTemplates({}, active) as unknown as {
    data: IModbusLibraryTemplate[] | null;
    isLoading: boolean;
  };

  // Existing names already in this site's library, per type, so a downloaded
  // template that collides gets a date-suffixed name instead of a silent
  // duplicate (same rule as the JSON-file import tabs, see libraryNaming.ts).
  const { data: existingDeviceTemplates } = useGetSiteModbusDeviceTemplates(siteId, active) as unknown as {
    data: { name: string }[] | null;
  };
  const { data: existingFunctionalTemplates } = useGetSiteModbusFunctionalTemplates(siteId, active) as unknown as {
    data: { name: string }[] | null;
  };
  const { data: existingUiTemplates } = useGetSiteModbusUiTemplates(siteId, active) as unknown as {
    data: { name: string }[] | null;
  };
  const existingNamesByType: Record<ModbusLibraryTemplateType, { name: string }[] | null> = {
    device: existingDeviceTemplates,
    functionalUnit: existingFunctionalTemplates,
    ui: existingUiTemplates,
  };

  const createErrorsRef = useRef<string[]>([]);
  const captureCreateError = (err: any) => {
    if (err?.message) {
      createErrorsRef.current.push(err.message);
    }
  };

  const { mutate: createDeviceTemplate } = useCreateModbusDeviceTemplate({ onError: captureCreateError });
  const { mutate: createFunctionalTemplate } = useCreateModbusFunctionalTemplate({ onError: captureCreateError });
  const { mutate: createUiTemplate } = useCreateModbusUiTemplate({ onError: captureCreateError });

  const items = data ?? [];
  const filtered = useMemo(
    () => items.filter((item) => (selectedTypes.size === 0 || selectedTypes.has(item.type)) && matchesSearch(item, search)),
    [items, search, selectedTypes],
  );

  const toggleBrowseMode = (mode: ModbusLibraryTemplateType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(mode)) {
        next.delete(mode);
      } else {
        next.add(mode);
      }
      return next;
    });
    setSelectedIds(new Set());
    setBanner(null);
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const createByType: Record<ModbusLibraryTemplateType, (payload: any) => Promise<any>> = {
    device: createDeviceTemplate,
    functionalUnit: createFunctionalTemplate,
    ui: createUiTemplate,
  };

  const handleDownload = async () => {
    if (!siteId) {
      return;
    }
    const ids = Array.from(selectedIds);
    if (!ids.length) {
      return;
    }

    setBanner(null);
    const total = ids.length;
    let done = 0;
    let jobsDone = 0;
    let jobsTotal = 0;
    setDownloadProgress({ done, total, jobsDone, jobsTotal });

    const idToItem = new Map(items.map((item) => [item.id, item]));
    const nameOf = (id: string) => idToItem.get(id)?.name ?? id;

    type Result = { name: string; failed: boolean; error?: string };
    const results: Result[] = [];

    const takenNamesByType: Record<ModbusLibraryTemplateType, Set<string>> = {
      device: new Set((existingNamesByType.device ?? []).map((item) => (item.name ?? "").trim().toLowerCase())),
      functionalUnit: new Set((existingNamesByType.functionalUnit ?? []).map((item) => (item.name ?? "").trim().toLowerCase())),
      ui: new Set((existingNamesByType.ui ?? []).map((item) => (item.name ?? "").trim().toLowerCase())),
    };

    // Fetch every template first so the total job (element) count is known
    // upfront, letting the creation phase report an accurate percentage.
    type Job = { type: ModbusLibraryTemplateType; element: Record<string, any> };
    const jobsByTemplate: Job[][] = [];
    const fetchFailed: boolean[] = [];
    const fetchErrors: (string | undefined)[] = [];
    for (const id of ids) {
      try {
        const entry = await fetchModbusLibraryTemplate(id);
        const elements = Array.isArray(entry.data) ? entry.data : [];
        jobsByTemplate.push(elements.map((element) => ({ type: entry.type, element })));
        fetchFailed.push(false);
        fetchErrors.push(undefined);
      } catch (err: any) {
        jobsByTemplate.push([]);
        fetchFailed.push(true);
        fetchErrors.push(err?.message);
      }
    }

    for (let i = 0; i < ids.length; i++) {
      if (fetchFailed[i]) {
        results.push({ name: nameOf(ids[i]), failed: true, error: fetchErrors[i] });
        done++;
        jobsDone = 0;
        jobsTotal = 0;
        setDownloadProgress({ done, total, jobsDone, jobsTotal });
        continue;
      }
      createErrorsRef.current = [];
      let templateFailed = false;
      jobsDone = 0;
      jobsTotal = jobsByTemplate[i].length;
      setDownloadProgress({ done, total, jobsDone, jobsTotal });
      // The library card's name/manufacturer/model are curated metadata on the
      // IModbusLibraryTemplate wrapper, and can legitimately differ from the
      // name/vendorName/modelName baked into the underlying device JSON (data[0]).
      // When a template holds a single element, that curated identity is what the
      // user selected off the browse card, so it — not the embedded JSON's own
      // fields — is what should land in the Site Library. Bundles of several
      // elements keep their own per-element names, since one wrapper name can't
      // describe all of them.
      const libraryItem = idToItem.get(ids[i]);
      const singleElement = jobsByTemplate[i].length === 1;
      for (const job of jobsByTemplate[i]) {
        const takenNames = takenNamesByType[job.type];
        const baseName = (singleElement && libraryItem?.name) || job.element?.name || "";
        const name = dedupeImportedName(baseName, takenNames);
        const payload: Record<string, any> = { ...job.element, name, site: siteId };
        if (job.type === "device" && singleElement) {
          if (libraryItem?.manufacturer) {
            payload.vendorName = libraryItem.manufacturer;
          }
          if (libraryItem?.model) {
            payload.modelName = libraryItem.model;
          }
        }
        const res = await createByType[job.type](payload);
        if (!res) {
          templateFailed = true;
        } else if (name) {
          takenNames.add(name.toLowerCase());
        }
        jobsDone++;
        setDownloadProgress({ done, total, jobsDone, jobsTotal });
      }
      results.push({ name: nameOf(ids[i]), failed: templateFailed, error: templateFailed ? createErrorsRef.current[0] : undefined });
      done++;
      setDownloadProgress({ done, total, jobsDone, jobsTotal });
    }

    setDownloadProgress(null);
    setSelectedIds(new Set());

    const succeeded = results.filter((r) => !r.failed);
    const failedResults = results.filter((r) => r.failed);

    const siteName = getSite(siteId)?.name || "";
    const addedCount = succeeded.length;
    const addedMessage =
      addedCount === 1
        ? t`1 library unit was added to the site's ${siteName} Modbus library`
        : t`${addedCount} library units were added to the site's ${siteName} Modbus library`;

    if (failedResults.length === 0) {
      setBanner({ type: "success", message: addedMessage });
    } else {
      const summary = t`${succeeded.length} of ${total} templates downloaded.`;
      const failedMessage = `${t`Failed`}: ${failedResults.map((r) => (r.error ? `${r.name} - ${r.error}` : r.name)).join("; ")}`;
      if (succeeded.length > 0) {
        const downloadedMessage = `${addedMessage}\n${succeeded.map((r) => r.name).join(", ")}.`;
        setBanner({ type: "success", message: downloadedMessage, secondaryType: "error", secondaryMessage: failedMessage });
      } else {
        setBanner({ type: "error", message: `${summary}\n${failedMessage}` });
      }
    }
  };

  return (
    <div className={styles.tabRoot}>
      <div className={styles.browsingRow}>
        <Typography variant="meta" color="muted" className={styles.browsingLabel}>
          {t`BROWSING:`}
        </Typography>
        <Row gap={8}>
          {BROWSE_MODES.map((mode) => (
            <Button
              key={mode.value}
              size="small"
              white={!selectedTypes.has(mode.value)}
              onClick={() => toggleBrowseMode(mode.value)}
            >
              {mode.label}
            </Button>
          ))}
        </Row>
      </div>

      <div className={styles.searchWrap}>
        <div className={styles.searchField}>
          <TextField
            fullWidth
            startIcon={<SearchIcon width={16} height={16} />}
            placeholder={t`Search by name, function, or description`}
            value={search}
            onChange={setSearch}
          />
        </div>
        {downloadProgress ? (
          <div className={styles.importProgress}>
            <Spinner size={14} />
            <Typography variant="meta">
              {t`Downloading`} {downloadProgress.done + 1} / {downloadProgress.total} (
              {downloadProgress.jobsTotal > 0 ? Math.round((downloadProgress.jobsDone / downloadProgress.jobsTotal) * 100) : 100}
              %)
            </Typography>
          </div>
        ) : (
          <Button size="medium" disabled={selectedIds.size === 0 || !siteId} onClick={handleDownload}>
            {t`Download to my Site Library`}
          </Button>
        )}
      </div>

      <LibraryActionBanner banner={banner} />

      <Typography variant="meta" color="muted" style={{ paddingBottom: 12 }}>
        {t`Select one or more items to download into your Site Library.`}
      </Typography>

      <div className={styles.tabBody}>
        {/* Spinner only on the first load — switching browse mode keeps the
            grid on screen instead of blanking the tab while it refetches. */}
        {isLoading && !data ? (
          <div className={styles.loaderWrap}>
            <Spinner size={28} />
          </div>
        ) : (
          <div className={styles.libraryGrid}>
            {filtered.map((item) => {
              const checked = !!item.id && selectedIds.has(item.id);
              return (
                <label key={item.id ?? item.name} className={`${styles.libraryCard} ${checked ? styles.libraryCardSelected : ""}`}>
                  <Row gap={10} align="flex-start">
                    <Checkbox checked={checked} value={item.id} onChange={() => item.id && toggleSelected(item.id)} />
                    <div className={styles.libraryCardBody}>
                      <Row justify="space-between" align="flex-start" gap={8}>
                        <Typography variant="cardTitle" className={styles.libraryCardTitle}>{item.name}</Typography>
                      </Row>
                      <div className={styles.libraryCardMeta}>
                        <span className={styles.libraryCardFieldLabel}>{t`Type`}:</span> {TYPE_LABEL[item.type]}
                      </div>
                      <div className={styles.libraryCardMeta}>
                        <span className={styles.libraryCardFieldLabel}>{t`Manufacturer`}:</span> {item.manufacturer || t`General`}
                      </div>
                      <div className={styles.libraryCardMeta}>
                        <span className={styles.libraryCardFieldLabel}>{t`Model`}:</span> {item.model || "-"}
                      </div>
                      <Typography variant="body2" color="muted" className={styles.libraryCardDescription}>
                        <span className={styles.libraryCardFieldLabel}>{t`Description`}:</span> {item.description || t`No Description`}
                      </Typography>
                    </div>
                  </Row>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <Typography variant="meta" color="muted">
                {t`No library items match your search.`}
              </Typography>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoolAutomationLibraryTab;
